import { computed, DestroyRef, inject, Injector, Type } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withFeature,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { Events, withEventHandlers } from '@ngrx/signals/events';
import { withGridConfigFeatures } from '@data-access/shared/store/features/with-grid-config-features.store';
import { GridType } from '@data-access/grid-views';
import { withDocumentTypeEntities } from '@data-access/documents/store/features/with-document-types.entities';
import { withMimeTypesEntities } from '@data-access/documents/store/features/with-mime-types.entities';
import {
  DocumentTypeExtended,
  EditDocument,
  UploadDocument,
  UploadDocumentPayload,
  UploadDocumentSignalState,
} from '@data-access/documents/types';
import {
  UPLOAD_DOCUMENT_GRID_CONFIG,
  uploadDocumentGridInitialState,
} from '@data-access/documents/contants';
import { withUploadDocumentEntities } from './entities';
import { UploadState } from '@data-access/submissions';
import { withGridCheckBoxFeature } from '@data-access/shared/store/features/with-grid-checkbox-feature.store';
import { DIALOG_TYPE, OpenDialogState } from '@data-access/shared';
import { DocumentsService } from '@data-access/documents/services/documents.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';
import { UPLOAD_DOCUMENT_EVENTS } from '../signal-events';

const _isActiveUpload = (uploadState: UploadState): boolean =>
  uploadState === 'uploading' || uploadState === 'completed';

export const uploadDocumentSignalStore = signalStore(
  withState(() => uploadDocumentGridInitialState),

  withProps(
    (
      store,
      _documentService = inject(DocumentsService),
      _destroyRef = inject(DestroyRef),
    ) => ({
      _injector: inject(Injector),
      _documentService,
      _destroyRef,
      _events: inject(Events),
    }),
  ),
  withFeature(({ _injector }) => withDocumentTypeEntities(_injector)),
  withFeature(({ _injector }) => withMimeTypesEntities(_injector)),
  withFeature(() => withGridCheckBoxFeature()),
  withFeature(() => withGridConfigFeatures()),
  withFeature(() => withUploadDocumentEntities()),
  withComputed(
    ({
      error,
      canCloseDialog,
      _uploadDocumentEntities,
      total,
      isLoading,
      _documentTypesEntities,
      _mimeTypeEntities,
      isAllSelected,
      isIndeterminate,
      selectedRows,
    }) => ({
      $error: computed(() => error()),
      $canDialogClose: computed(() => canCloseDialog()),
      $documentTypes: computed(() => {
        const _documentTypes = _documentTypesEntities();
        if (!_documentTypes) {
          return [] as DocumentTypeExtended[];
        }
        return _documentTypes;
      }),
      $mimeTypeEntities: computed(() => {
        const entities = _mimeTypeEntities();
        return entities || [];
      }),
      $documents: computed(() => {
        const _documents = _uploadDocumentEntities();
        if (!_documents) {
          return [] as UploadDocument[];
        }
        return _documents;
      }),
      $documentUploaded: computed(() => {
        const _documents = _uploadDocumentEntities();
        if (!_documents) {
          return false;
        }
        return _documents.every(
          (doc) => !['pending', 'uploading'].includes(doc.uploadState),
        );
      }),
      $confirmedUploadDocuments: computed(() => {
        const _documents = _uploadDocumentEntities();
        if (!_documents) {
          return [] as UploadDocument[];
        }
        return _documents.filter(({ uploadConfirmed }) => uploadConfirmed);
      }),
      $uploadFailedDocuments: computed(() => {
        const _documents = _uploadDocumentEntities();
        if (!_documents) {
          return [] as UploadDocument[];
        }
        return _documents.filter((doc) => doc.uploadState === 'failed');
      }),
      $isLoading: computed(() => isLoading()),
      $totalCount: computed(() => total()),
      $gridType: computed(() => 'submissionUploadDocuments' as GridType),
      $isAllSelected: computed(() => isAllSelected()),
      $isIndeterminate: computed(() => isIndeterminate()),
      $selectedRows: computed(() => selectedRows()),
    }),
  ),
  withMethods(({ _documentService, _destroyRef, ...store }) => {
    const _loadDocumentGrid = (documents: UploadDocument[]) => {
      store.loadUploadDocument(documents);
      store.loadConfigurations(UPLOAD_DOCUMENT_GRID_CONFIG);
      store.setGridViews([]);
      store.setAllRows(store.selectedRows(), documents);
      patchState(store, {
        total: documents.length,
      });
    };

    const _updateDocumentGrid = (document: UploadDocument) => {
      store.applyDocumentGridUpdate(document);
      patchState(store, {
        isLoading: false,
        canCloseDialog: true,
      });
    };

    const _updateFileStatus = (
      fileName: string,
      uploadState: UploadState,
      uploadError: string | null = null,
    ) => {
      const _documents = store.$documents();
      const _updateDoc = _documents.find((doc) => doc.file.name === fileName);

      if (_updateDoc) {
        store.applyDocumentGridUpdate({
          ..._updateDoc,
          uploadError,
          uploadState,
        });
      }
    };

    const _updateDocuments = (editDocument: EditDocument) => {
      const _documents = store.selectedRows();
      _documents.forEach((doc) => {
        doc.taskType.get('value')?.setValue(editDocument.tasks || []);
        doc.documentType
          .get('value')
          ?.setValue(editDocument.document_type || null);
        doc.document_type_name = editDocument.document_type?.label || '';
        _updateDocumentGrid(doc);
      });
      patchState(store, {
        selectedRows: [],
        isAllSelected: false,
        isIndeterminate: false,
      });
    };

    const _openEditDocumentDialog = (dialogMetaData: OpenDialogState) => {
      store.openDialog({
        component: () => dialogMetaData.component(),
        componentData: dialogMetaData.componentData,
        type: DIALOG_TYPE.GroupFields,
      });
    };

    const _uploadDocuments = (documents: UploadDocumentPayload[]) => {
      documents.forEach((doc) => {
        _updateFileStatus(doc.file.name, 'uploading');
        _documentService
          .uploadDocuments(doc)
          .pipe(takeUntilDestroyed(_destroyRef))
          .subscribe({
            error: (err) => {
              _updateFileStatus(doc.file.name, 'failed', err);
            },
            next: () => _updateFileStatus(doc.file.name, 'completed'),
          });
      });
    };

    return {
      loadDocumentGrid: (documents: UploadDocument[]) =>
        _loadDocumentGrid(documents),
      updateDocumentGrid: (document: UploadDocument) =>
        _updateDocumentGrid(document),
      loadDocumentTypes: (params: { workspaceId?: string }) => {
        patchState(store, {
          isLoading: false,
          currentStateDestroyed: false,
        });
        store.setDocumentTypes(params);
      },
      loadMimeTypes: () => {
        store.setMimeTypes();
      },
      resetGridSettings: () => {},
      resetState: () => {
        patchState(store, {
          ...uploadDocumentGridInitialState,
          currentStateDestroyed: true,
          error: null,
          isLoading: false,
        });
        store.loadUploadDocument([]);
      },
      selectAll: (checked: boolean) =>
        store.applySelectAll(store.selectedRows(), store.allRows(), checked),

      selectRow: (row: UploadDocument, checked: boolean) =>
        store.applySelectRow(
          store.selectedRows(),
          store.allRows(),
          row,
          checked,
        ),

      updateRows: (rows: UploadDocument[]) => {
        patchState(store, {
          selectedRows: [],
          isAllSelected: false,
          isIndeterminate: false,
          allRows: rows,
        });
        _loadDocumentGrid(rows);
      },

      openEditDocumentDialog: (dialogMetaData: OpenDialogState) => {
        patchState(store, { canCloseDialog: false });
        _openEditDocumentDialog(dialogMetaData);
      },

      updateDocument: (document: EditDocument) => _updateDocuments(document),
      uploadDocuments: (documents: UploadDocumentPayload[]) =>
        _uploadDocuments(documents),
      resetSelectedRows: () =>
        patchState(store, {
          selectedRows: [],
          isAllSelected: false,
          isIndeterminate: false,
        }),
      updateFileStatus: (
        fileName: string,
        uploadState: UploadState,
        error?: string | null,
      ) => _updateFileStatus(fileName, uploadState, error),
    };
  }),
  withEventHandlers(({ _events, ...store }) => ({
    markWebSocketCompletedDocument$: _events
      .on(UPLOAD_DOCUMENT_EVENTS.documentUploadStatusReceived)
      .pipe(
        tap(({ payload: { documentTitle, status } }) => {
          if (status !== 'completed' || !documentTitle) {
            return;
          }

          const document = store
            .$documents()
            .find(
              ({ file, uploadConfirmed, uploadState }) =>
                file.name === documentTitle &&
                _isActiveUpload(uploadState) &&
                !uploadConfirmed,
            );
          if (document) {
            store.applyDocumentGridUpdate({
              ...document,
              uploadConfirmed: true,
            });
          }
        }),
      ),
    markWebSocketFailedDocuments$: _events
      .on(UPLOAD_DOCUMENT_EVENTS.uploadFailedDocumentsReceived)
      .pipe(
        tap(({ payload: { failedDocuments } }) => {
          const failedFileNames = new Set(
            failedDocuments.map(({ title }) => title),
          );

          for (const document of store.$documents()) {
            if (
              failedFileNames.has(document.file.name) &&
              _isActiveUpload(document.uploadState)
            ) {
              store.applyDocumentGridUpdate({
                ...document,
                uploadError: null,
                uploadState: 'failed',
              });
            }
          }
        }),
      ),
  })),
) satisfies Type<UploadDocumentSignalState>;
